import { useEffect, useMemo, useRef, useState } from 'react';
import type { MermaidErrorInfo, PipelineStage, Theme } from '../store/types.ts';
import { renderMermaidToSvg } from '../lib/mermaid-config.ts';

type RenderResult = {
  svg: string;
  error: MermaidErrorInfo | null;
  codeKey: string;
};

export function useMermaidRenderer(
  mermaidCode: string | undefined,
  theme: Theme,
  pipelineStage: PipelineStage,
  onPipelineChange: (stage: PipelineStage) => void,
  onStatusChange: (text: string) => void,
) {
  const code = useMemo(() => mermaidCode?.trim() || '', [mermaidCode]);
  const codeKey = `${code}\0${theme}`;
  const [result, setResult] = useState<RenderResult>({ svg: '', error: null, codeKey: '' });
  const requestSeqRef = useRef(0);
  const pipelineStageRef = useRef(pipelineStage);

  useEffect(() => {
    pipelineStageRef.current = pipelineStage;
  }, [pipelineStage]);

  useEffect(() => {
    if (!code) return;

    const seq = ++requestSeqRef.current;
    const key = `${code}\0${theme}`;

    renderMermaidToSvg(code, theme).then((rendered) => {
      if (requestSeqRef.current !== seq) return;

      const stage = pipelineStageRef.current;
      if (rendered.error || !rendered.svg) {
        setResult({ svg: '', error: rendered.error || { raw: 'Unknown Mermaid render error.' }, codeKey: key });
        onStatusChange('Render failed');
        if (stage !== 'error' && stage !== 'cancelled') {
          onPipelineChange('error');
        }
      } else {
        setResult({ svg: rendered.svg, error: null, codeKey: key });
        onStatusChange('Ready');
        if (stage === 'rendering') {
          onPipelineChange('completed');
        }
      }
    });
  }, [code, theme, onPipelineChange, onStatusChange]);

  // Derive isRendering: true when code exists but result is for a different code/theme combo
  const isRendering = code !== '' && result.codeKey !== codeKey;

  // When code is empty, return empty result
  const finalResult = code ? result : { svg: '', error: null, codeKey: '' };

  return { renderedSvg: finalResult.svg, renderError: finalResult.error, isRendering };
}
